import { Mobile } from '@bimser/common';
import {
	BCDivider,
	BCExtremeGridEntities,
	BCLoading,
	BCTreeSelect,
	BCTreeSelectEntities,
	BCTreeSelectRef,
	findMessage
} from '@bimser/components';
import { ITreeData } from '@bimser/components/dist/BCTreeSelect/entities';
import { FormItemTypes, IDictionary, isNullOrUndefined } from '@bimser/core';
import IconRefresh from '@bimser/icons/16/refresh';
import React, { JSX } from 'react';
import {
	DataSourceType,
	ICSSProperties,
	IDataSource,
	IOnLoadArgs,
	IPropertyField,
	TreeSelectStrategy
} from '../../../../entities/common';
import { getDataSourceData } from '../../../../helper';
import EditControlBase from '../../base/EditControlBase';
import { IDataLoadEventArgs } from '../../entities/EventArgs';
import Styles from './assets/styles.scss';
import { ITreeSelect } from './entities/ITreeSelect';
import { ITreeSelectProperties } from './entities/ITreeSelectProperties';
import { Liquid } from 'liquidjs';
const liquidEngine = new Liquid();
import _isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
const dropdownRenderDivider: ICSSProperties = { margin: '0' };

type UnknownTreeData = IDictionary<any> & { children?: UnknownTreeData };

export default class TreeSelect extends EditControlBase<string[]> {
	allowClear: boolean;
	showSearch: boolean;
	multiple: boolean;
	checkable: boolean;
	defaultExpandAll: boolean;
	checkedStrategy: TreeSelectStrategy;
	defaultExpandedKeys: string[];
	treeItems: ITreeSelect[];
	dataSource: IDataSource;
	dataSourceType: DataSourceType;
	notRecursive?: boolean;
	childrenKey?: string;
	parentKey?: string;
	textSeparator: string;
	valueSeparator: string;
	hierarchyType?: string;
	hasItemExpr: string;
	inputRef = React.createRef<BCTreeSelectRef>();
	hideForceRefreshButton: boolean;

	__dataSourceLoadedAtLeastOnce?: boolean;
	__searchValue: string = '';
	__data?: any[] = [];
	__treeItems?: ITreeSelect[] = [];
	__startupOptions?: BCExtremeGridEntities.ILoadOptions = null;
	__value?: any[] = [];
	__currentParameters?: any = {};

	constructor() {
		super();
		this.controlType = FormItemTypes.EControlTypes.TreeSelect;
		this.allowClear = false;
		this.showSearch = false;
		this.multiple = false;
		this.checkable = false;
		this.defaultExpandAll = false;
		this.checkedStrategy = 'all';
		this.defaultExpandedKeys = [];
		this.treeItems = [];
		this.dataSourceType = 'dynamic';
		this.textSeparator = ',';
		this.valueSeparator = ',';
		this.dataSource = {
			fileId: null,
			displayExpr: [],
			valueExpr: null,
			name: null,
			runAtServer: false,
			parameters: [],
			useClientCachedData: true
		};
		this.notRecursive = true;
		this.childrenKey = '';
		this.parentKey = '';
		this.hierarchyType = null;
		this.hideForceRefreshButton = false;

		this.__onLoadTreeData = this.__onLoadTreeData.bind(this);
		this.__getIsLeafStatus = this.__getIsLeafStatus.bind(this);
		this.__getSelectedItemKeysByMutationStatus =
			this.__getSelectedItemKeysByMutationStatus.bind(this);
		this.__dropdownRender = this.__dropdownRender.bind(this);
		this.__renderDropdownFooter = this.__renderDropdownFooter.bind(this);

		this.subscribe('trigger:OnClick', (value: string) => {
			this.onChange(JSON.parse(value));
		});
	}

	__loadProperties(data: ITreeSelectProperties) {
		super.__loadProperties(data);
		this.__proxyChanged.allowClear = data.allowClear;
		this.__proxyChanged.showSearch = data.showSearch;
		this.__proxyChanged.multiple = data.multiple;
		this.__proxyChanged.checkable = data.checkable;
		this.__proxyChanged.defaultExpandAll = data.defaultExpandAll;
		this.__proxyChanged.checkedStrategy = data.checkedStrategy;
		this.__proxyChanged.defaultExpandedKeys = data.defaultExpandedKeys;
		this.__proxyChanged.treeItems = data.treeItems;
		this.__proxyChanged.dataSource = data.dataSource;
		this.__proxyChanged.dataSourceType = data.dataSourceType;
		this.__proxyChanged.childrenKey = data.childrenKey;
		this.__proxyChanged.parentKey = data.parentKey;
		this.__proxyChanged.textSeparator = data.textSeparator;
		this.__proxyChanged.valueSeparator = data.valueSeparator;
		this.__proxyChanged.hierarchyType = data.hierarchyType;
		this.__proxyChanged.notRecursive = data.notRecursive;
		this.__proxyChanged.hasItemExpr = data.hasItemExpr;
		this.__proxyChanged.hideForceRefreshButton = data.hideForceRefreshButton;
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			allowClear: this.allowClear && !this.readOnly,
			showSearch: this.showSearch,
			multiple: this.multiple,
			checkable: this.checkable,
			defaultExpandAll: this.defaultExpandAll,
			checkedStrategy: this.checkedStrategy,
			defaultExpandedKeys: this.defaultExpandedKeys,
			treeItems: this.treeItems,
			dataSource: this.dataSource,
			dataSourceType: this.dataSourceType,
			notRecursive: this.notRecursive,
			childrenKey: this.childrenKey,
			parentKey: this.parentKey,
			textSeparator: this.textSeparator,
			valueSeparator: this.valueSeparator,
			hideForceRefreshButton: this.hideForceRefreshButton,
			value: this.__getSelectedItemKeysByMutationStatus()
		};
	}

	__getPropertyFields(): IPropertyField[] {
		const fields = super.__getPropertyFields();
		return [
			...fields,
			{
				name: 'value',
				type: 'string',
				common: true
			},
			{
				name: 'allowClear',
				type: 'boolean'
			},
			{
				name: 'showSearch',
				type: 'boolean'
			},
			{
				name: 'multiple',
				type: 'boolean'
			},
			{
				name: 'checkable',
				type: 'boolean',
				common: true
			},
			{
				name: 'defaultExpandAll',
				type: 'boolean'
			},
			{
				name: 'parentKey',
				type: 'string'
			},
			{
				name: 'childrenKey',
				type: 'string'
			},
			{
				name: 'textSeparator',
				type: 'string'
			},
			{
				name: 'valueSeparator',
				type: 'string'
			},
			{
				name: 'hierarchyType',
				type: 'string'
			},
			{
				name: 'checkedStrategy',
				type: 'enum',
				enumMembers: ['all', 'child', 'parent'] as TreeSelectStrategy[]
			},
			{
				name: 'hideForceRefreshButton',
				type: 'boolean'
			}
		];
	}

	focus() {
		this.inputRef.current?.focus();
	}

	/* ***ON_LOAD*** */

	__onLoad({ externalData, reload, forceRefresh }: IOnLoadArgs) {
		if (!this.__isInitialized && this.dataSource?.parameters?.length && !reload) {
			return;
		}

		if (
			this.dataSourceType == 'dynamic' &&
			this.__form?.serviceUrl &&
			this.dataSource?.name &&
			!this.dataSource?.runAtServer
		) {
			if (!isEqual(this.__currentParameters, externalData?.fieldParams || {})) {
				this.__currentParameters = { ...(externalData?.fieldParams || {}) };
				this.__proxyChanging.value = [];
				this.__value = [];
			}

			this.__onLoadTreeData(externalData, reload, forceRefresh);
		}
	}

	reload(forceRefresh?: boolean) {
		if (this.dataSource?.name) {
			if (this.dataSource?.parameters.length) {
				this.__reloadDataSource(this.dataSource, forceRefresh).catch(() => {
					this.__proxyChanging.loading = false;
				});
			} else {
				this.__onLoad({ reload: true, forceRefresh });
			}
		}
	}

	__onLoadTreeData(externalData: any, reload: boolean, forceRefresh: boolean) {
		this.__proxyChanging.loading = true;

		getDataSourceData({
			dataSource: this.dataSource,
			projectName: this.__form.projectName,
			clientCacheId: this.__form.__windowId,
			fieldParams: externalData?.fieldParams,
			reload,
			loadOptions: { pagination: { skip: 0, take: 100 }, sorts: null, filters: [] },
			forceRefresh,
			projectVersion: this.__form.packageVersion
		})
			.then(response => {
				if (response?.data?.result?.length) {
					const tId = this.__form.beginTransaction();

					this.__data = response.data.result;
					const treeItems = this.__overrideTreeItems(this.__data);
					this.__proxyChanging.treeItems = treeItems;
					if (!this.__dataSourceLoadedAtLeastOnce) {
						this.__proxyChanging.value =
							this.__getSelectedItemKeysByMutationStatus(treeItems);
					}

					this.__form.endTransaction(tId).then(() => {
						this.__proxyChanging.loading = false;
						this.__dataSourceLoadedAtLeastOnce = true;
						this.emit('event:OnDataLoad', { data: this.__data } as IDataLoadEventArgs);
					});
				} else {
					this.__proxyChanging.loading = false;
				}
			})
			.catch(() => {
				this.__data = [];
				this.__proxyChanging.treeItems = [];
				this.__proxyChanging.loading = false;
				this.__proxyChanging.value = [];
				this.__value = [];
			});
	}
	/* ***ON_LOAD*** */

	/* ***CONVERT_DATA*** */
	__overrideTreeItems(data: any[]) {
		if (this.hierarchyType === 'parentKey' && this.parentKey !== '') {
			const treeData =
				this.notRecursive && !this.__searchValue
					? this.__convertRecursiveDataForParentHierarchy(data)
					: data;
			return this.__getTreeSelectItems(treeData);
		} else if (this.hierarchyType === 'childrenKey' && this.childrenKey !== '') {
			const treeData =
				this.notRecursive && !this.__searchValue
					? this.__convertRecursiveDataForParentHierarchy(data)
					: data;
			return this.__getTreeSelectItems(treeData, this.childrenKey);
		} else if (_isEmpty(this.parentKey) && _isEmpty(this.childrenKey)) {
			return this.__getTreeDataWithoutRelations(data);
		}

		return this.__getTreeItemsByRelations(data);
	}

	__getTreeItemsByRelations(data: any[]) {
		let treeItems = [];
		if (_isEmpty(this.parentKey) && _isEmpty(this.childrenKey)) {
			treeItems = this.__getTreeDataWithoutRelations(data);
		} else if (this.notRecursive) {
			if (!_isEmpty(this.parentKey) && _isEmpty(this.childrenKey)) {
				treeItems = this.__getTreeSelectItems(
					this.__convertRecursiveDataForParentHierarchy(data)
				);
			} else if (_isEmpty(this.parentKey) && !_isEmpty(this.childrenKey)) {
				treeItems = this.__getTreeSelectItems(
					this.__convertRecursiveDataForChildHierarchy(data)
				);
			}
		} else if (
			(!_isEmpty(this.parentKey) && _isEmpty(this.childrenKey)) ||
			(_isEmpty(this.parentKey) && !_isEmpty(this.childrenKey))
		) {
			treeItems = this.__getTreeSelectItems(data, this.childrenKey);
		}

		return treeItems;
	}

	__getTreeSelectItems(data: Array<any>, childrenKey: string = 'children') {
		if (Array.isArray(data)) {
			return data.map(i => ({
				children: this.__getTreeSelectItems(i[childrenKey], childrenKey),
				text: this.__getTextByDisplayExpression(data, i),
				parentKey: i[this.parentKey],
				key: i[this.dataSource.valueExpr],
				isLeaf: this.__getNodeExpandStatus() || this.__getIsLeafStatus(i),
				selected: this._value?.length
					? this._value.includes(i[this.dataSource.valueExpr])
					: i.selected
			}));
		} else {
			return [];
		}
	}

	__convertRecursiveDataForParentHierarchy(data: UnknownTreeData[]): Array<any> {
		const recursiveData = new Array<UnknownTreeData>();

		data.forEach(item => {
			if (
				isNullOrUndefined(item[this.parentKey]) ||
				item[this.parentKey] === '' ||
				item[this.parentKey] === 0
			) {
				recursiveData.push({
					...item,
					children: !isNullOrUndefined(item[this.dataSource.valueExpr])
						? this.__getChildsForParentHierarchy(data, item[this.dataSource.valueExpr])
						: []
				});
			}
		});

		return recursiveData;
	}

	__getChildsForParentHierarchy(
		data: UnknownTreeData[] = [],
		parentId: string | number
	): Array<any> {
		return data
			.map(i => {
				if (i[this.parentKey] === parentId) {
					return {
						...i,
						children: this.__getChildsForParentHierarchy(
							data,
							i[this.dataSource.valueExpr]
						)
					};
				}
			})
			.filter(Boolean);
	}

	__getChildsForChildHierarchy(data: UnknownTreeData = {}) {
		if (this.childrenKey.includes('.')) {
			let _data = { ...data };
			this.childrenKey.split('.').forEach(x => {
				if (_data) {
					_data = _data[x];
				}
			});
			return _data;
		}
		return data[this.childrenKey];
	}

	__convertRecursiveDataForChildHierarchy(data: UnknownTreeData[] = []) {
		const recursiveData = new Array<UnknownTreeData>();

		data.forEach(item => {
			const findChild = this.__getChildsForChildHierarchy(item);
			let childs: UnknownTreeData[] = [];

			if (Array.isArray(findChild)) {
				findChild.forEach(
					() => (childs = [...this.__convertRecursiveDataForChildHierarchy(findChild)])
				);
			} else {
				childs = this.__convertRecursiveDataForChildHierarchy(findChild);
			}

			recursiveData.push({
				...item,
				children: childs
			});
		});

		return recursiveData;
	}

	__getTreeDataWithoutRelations(data: Array<any>) {
		const selectedItemKeys = [];

		const items = data.map(item => {
			const key = item[this.dataSource.valueExpr];
			const selected = Boolean(
				this.__getSelectedItemKeysByMutationStatus().find(
					treeItemKey => treeItemKey === key?.toString()
				)
			);

			if (selected) {
				selectedItemKeys.push(key);
			}

			return {
				children: null,
				parentKey: null,
				key,
				text: this.__getTextByDisplayExpression(data, item),
				selected
			};
		});

		this.__proxyChanging.value = selectedItemKeys;

		return items;
	}

	__getTextByDisplayExpression(data: any[], item: any) {
		let displayFormat = this.dataSource.displayFormat;

		if (displayFormat.includes('.__userCulture')) {
			displayFormat = displayFormat.replace(
				'.__userCulture',
				`['${this.__form.currentLanguage.culture}']`
			);
		}

		return liquidEngine.parseAndRenderSync(displayFormat, item);
	}

	__getSelectedItemKeysByMutationStatus(treeItems: ITreeSelect[] = this.treeItems) {
		const beforeMutated = treeItems.every(treeItem => isNullOrUndefined(treeItem.selected));

		if (this.dataSourceType === 'static') {
			return this.value?.map(key => key.toString()) || [];
		}

		if (beforeMutated) {
			return treeItems.map(treeItem => treeItem.key?.toString());
		} else {
			const selectedKeys = [];
			this.__getSelectedItemKeys(treeItems, selectedKeys);
			return selectedKeys.map(key => key.toString());
		}
	}

	/* ***CONVERT_DATA*** */

	getTreeSelectStrategy(): BCTreeSelectEntities.treeSelectShowCheckedStrategy {
		switch (this.checkedStrategy) {
			case 'all':
				return BCTreeSelectEntities.treeSelectShowCheckedStrategy.SHOW_ALL;
			case 'parent':
				return BCTreeSelectEntities.treeSelectShowCheckedStrategy.SHOW_PARENT;
			case 'child':
				return BCTreeSelectEntities.treeSelectShowCheckedStrategy.SHOW_CHILD;
			default:
				return BCTreeSelectEntities.treeSelectShowCheckedStrategy.SHOW_ALL;
		}
	}

	__getSelectedItemKeys(items: Array<ITreeSelect>, selectedKeys: string[]) {
		if (items && Array.isArray(items)) {
			for (const item of items) {
				if (item.selected) {
					selectedKeys.push(item.key);
				}

				this.__getSelectedItemKeys(item.children, selectedKeys);
			}
		}
	}

	__getNodeExpandStatus() {
		if (this.__searchValue) {
			return true;
		}
		if (
			this.__form.__fromMobile &&
			this.__form.__mobileFormView === Mobile.MobileFormView.NativePlus
		) {
			return undefined;
		}
		if (this.dataSource.name && this.notRecursive && this.hierarchyType !== 'childrenKey') {
			return false;
		}
		return undefined;
	}

	__getIsLeafStatus(item: any) {
		if (this.hierarchyType === 'parentKey') {
			if (typeof item[this.hasItemExpr] === 'undefined') {
				return false;
			}
			return !item[this.hasItemExpr];
		}

		if (this.hierarchyType === 'childrenKey') {
			const childs = item[this.childrenKey];
			if (typeof childs === 'undefined') {
				return true;
			} else if (Array.isArray(childs) && !childs.length) {
				return true;
			}
			return false;
		}
	}

	__getTreeItems(items: ITreeSelect[] = []): ITreeData[] {
		return items?.map<ITreeData>(i => {
			const key = i.key?.toString();
			return {
				key,
				value: key,
				selectable: this.dataSourceType === 'dynamic' || i.selectable,
				title: i.text,
				children: this.__getTreeItems(i.children),
				isLeaf: i.isLeaf,
				disableCheckbox: this.dataSourceType !== 'dynamic' && !i.selectable
			};
		});
	}

	__setSelectedKeys(treeItems: ITreeSelect[] = this.treeItems, selectedItems: string[]): any {
		return Array.isArray(treeItems)
			? treeItems.map(item => ({
					...item,
					selected: selectedItems.includes(item.key.toString()),
					children: this.__setSelectedKeys(item.children, selectedItems)
			  }))
			: [];
	}

	onChange(value: string[] = []) {
		const selectedItems: string[] = Array.isArray(value) ? value : [value];
		this.__proxyChanging.value = selectedItems;
		this.__proxyChanging.treeItems = this.__setSelectedKeys(undefined, selectedItems);
	}

	__renderDropdownFooter() {
		if (!this.hideForceRefreshButton) {
			return (
				<>
					<BCDivider styles={dropdownRenderDivider} />
					<div className={Styles.dropdownRender}>
						<a onClick={() => this.reload(true)}>
							<IconRefresh />
							{findMessage.get('102660')}
						</a>
					</div>
				</>
			);
		}
	}

	__dropdownRender(menu: React.ReactElement) {
		return (
			<div>
				{menu}
				{this.__renderDropdownFooter()}
			</div>
		);
	}

	__render(children?: JSX.Element) {
		return super.__render(
			<BCTreeSelect
				ref={this.inputRef}
				className={this.style.backgroundColor ? Styles.TreeSelectBackgroundClass : ''}
				value={this.__getSelectedItemKeysByMutationStatus()}
				treeData={this.__getTreeItems(this.treeItems)}
				allowClear={this.allowClear}
				showSearch={this.showSearch}
				multiple={this.checkable || this.multiple}
				treeCheckable={this.checkable}
				treeDefaultExpandAll={this.defaultExpandAll}
				disabled={this.__isDisabled()}
				showCheckedStrategy={this.getTreeSelectStrategy()}
				treeDefaultExpandedKeys={this.defaultExpandedKeys}
				style={{
					...this.style,
					...({ backgroundColor: this.style.backgroundColor } as any)
				}}
				onChange={this.onChange.bind(this)}
				readOnly={this.__isReadOnly()}
				treeNodeFilterProp="title"
				dropdownRender={this.dataSource.name && this.__dropdownRender}
				notFoundContent={this.loading ? <BCLoading size="small" /> : null}
				dropdownClassName={Styles.treeSelectDropdownStyles}
			/>
		);
	}
}
